import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { switchMap, catchError, combineLatest } from 'rxjs';
import { of } from 'rxjs';
import { Router } from '@angular/router';
import { aggregateBoundProducts } from '../../utils/aggregate-bound-products';
import { buildProductTableData } from '../../utils/build-product-table-data';
import { formatLocalIsoDate, formatUtcIsoDate } from '../../utils/iso-date';
import {
  BadgeComponent,
  CardActionsComponent,
  CardBodyComponent,
  CardComponent,
  CardFooterAction,
  CardHeaderComponent,
  CardTitleBadge,
  GroundworksTableComponent,
  GroundworksTableData,
  TableGroup,
} from '@groundworks/ngx-groundworks-ui';
import { CardHeaderTitleDirective } from '@groundworks/ngx-groundworks-ui';
import { CompensationTabsComponent } from '../compensation-tabs/compensation-tabs.component';
import { ConfigService } from '../../config.service';
import { ProjectCheckpointTypeConstants } from '../../constants/project-checkpoint-type.constants';
import { ProjectStatusConstants } from '../../constants/project-status.constants';
import { ProjectTypeConstants } from '../../constants/project-type.constants';
import {
  ApiClient,
  BoundProductInfo,
  JobInfo,
  PermitListItem,
  PermitStatus,
  ProjectCheckpointInfo,
  ProjectCheckpointResponse,
  ProjectInfo,
  ProjectInvoiceStatus,
  ProjectSpecialNeedInfo,
  ProjectUtilityInfo,
  TaxInfo,
  UpdateProjectSpecialNeedRequest,
  UpdateProjectUtilitiesNotNeededRequest,
  UpdateProjectUtilityRequest,
} from '../../sdk/sdk';
import { ModalService } from '../../services/modal.service';

/**
 * One of the card header's two status pills (Project Status, Invoice Status), plus the date
 * rendered beside it.
 *
 * `date` is null wherever no date belongs next to the pill: the states that have no stamp on the
 * wire at all (Cancelled, Descoped), and a state whose date field came back unpopulated. The
 * template omits the date element entirely on null rather than rendering an empty slot, so null and
 * "absent" are the same thing to a reader — which is why this is `Date | null` and not optional.
 */
type StatusBadge = CardTitleBadge & { date: Date | null };

@Component({
  selector: 'app-project',
  imports: [
    BadgeComponent,
    CardComponent,
    CardBodyComponent,
    CardActionsComponent,
    CardHeaderComponent,
    CardHeaderTitleDirective,
    CompensationTabsComponent,
    DatePipe,
    GroundworksTableComponent,
  ],
  templateUrl: './project.component.html',
  styleUrl: './project.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectComponent {
  private readonly modalService = inject(ModalService);
  private readonly apiClient = inject(ApiClient);
  private readonly router = inject(Router);
  readonly configService = inject(ConfigService);

  get enableCommissions(): boolean {
    return this.configService.enableCommissions;
  }

  get enablePpj(): boolean {
    return this.configService.enablePpj;
  }

  readonly project = input.required<ProjectInfo>();
  readonly branchId = input.required<string>();
  readonly opportunityId = input.required<string>();
  // Job context for the split modal: taxes drive its live tax preview and the categorized
  // product lists supply the category group headers (MOD-3972).
  readonly job = input<JobInfo | undefined>(undefined);
  readonly percentProjectPaid = input<number | null>(null);
  readonly acr = input<number | null>(null);
  // A job can have several projects; these default to 1-of-1 so a caller that doesn't pass them
  // (e.g. most existing tests) still renders a sensible label rather than "undefined of undefined".
  readonly projectPosition = input<number>(1);
  readonly projectCount = input<number>(1);
  readonly positionLabel = computed(() => `${this.projectPosition()} of ${this.projectCount()}`);
  readonly checkpointCompleted = output<ProjectCheckpointResponse>();
  readonly permitSaved = output<void>();
  readonly compensationTab = signal<'project' | 'ppj' | 'commissions'>('project');
  readonly compensationForcedView = computed<'commissions' | 'ppj' | null>(() => {
    const tab = this.compensationTab();
    return tab === 'project' ? null : tab;
  });
  readonly compensation = viewChild(CompensationTabsComponent);
  readonly commissionFooterActions = computed<CardFooterAction[]>(
    () => this.compensation()?.commissionFooterActions() ?? [],
  );
  readonly ppjFooterActions = computed<CardFooterAction[]>(
    () => this.compensation()?.ppjFooterActions() ?? [],
  );
  readonly isProjectCancelled = computed(() => this.project()?.isCancelled ?? false);
  readonly isFullyDescoped = computed(() => this.project()?.isFullyDescoped ?? false);
  /**
   * The project's RECORDED disposition, which is not the same question as {@link isFullyDescoped}.
   *
   * `isFullyDescoped` is bound-product arithmetic — every product nets to zero AND at least one
   * product row exists to sum. A project emptied to zero rows and then descoped satisfies the status
   * but never the arithmetic, so the math alone left the card fully actionable on work that no longer
   * exists (MOD-4990). The two are kept separate rather than merged because `isFullyDescoped` is also
   * true for a zeroed project deliberately SPARED from disposal for anchoring a live tax, which
   * carries no Descoped stamp.
   */
  readonly isDescoped = computed(
    () => this.project()?.projectStatusId === ProjectStatusConstants.Descoped,
  );
  readonly isProjectComplete = computed(() => this.project()?.completionDate != null);
  /**
   * Whether this project's payoff has been RESOLVED as paid — the sole gate on the PAID IN FULL
   * watermark and on suppressing the redundant "Pending" pill.
   *
   * The three wire values mean different things and only one of them may render a money claim:
   * - `true` — resolved, and this project is paid off.
   * - `false` — resolved, and it is not paid: either no payoff ledger row, or a row exists but the
   *   project is cancelled and the server's predicate suppresses it.
   * - JSON `null` — the read path serving this payload never queried the payoff ledger, so nothing
   *   is known either way.
   *
   * Hence `=== true`, not truthiness and emphatically not `!== undefined`. `nswag.json` sets
   * `nullValue: "Undefined"`, so the generated type says `boolean | undefined` while the deserialized
   * runtime value is the JSON `null` and the assignment in `sdk.ts` is a raw passthrough with no
   * coercion — `!== undefined` would report an unresolved field as resolved, which is the one failure
   * direction that invents a payment. Every other reading of the value merely omits the watermark.
   *
   * The underlying ledger is first-payoff-wins and is never un-stamped, so a refund, chargeback or
   * post-payoff change-order upsell does NOT clear this. Read the watermark as "a payment once paid
   * this project off", never as a live balance.
   */
  readonly isPaidInFull = computed(() => this.project()?.isPaidInFull === true);
  readonly isReadOnly = computed(
    () => this.isProjectCancelled() || this.isFullyDescoped() || this.isDescoped(),
  );
  readonly isCheckpointDisabled = computed(() => {
    if (this.isProjectComplete()) return true;
    const dates = this.project()?.installDates;
    if (!dates || dates.length === 0) return true;
    const earliest = dates.reduce((min, d) => d.date < min.date ? d : min).date;
    const earliestStr = formatUtcIsoDate(earliest);
    const todayStr = formatLocalIsoDate(new Date());
    return earliestStr < todayStr;
  });
  readonly hasInstallDates = computed(() => {
    const dates = this.project()?.installDates;
    return !!dates && dates.length > 0;
  });
  readonly hasFutureInstallDates = computed(() => {
    const dates = this.project()?.installDates;
    if (!dates || dates.length === 0) return false;
    const todayStr = formatLocalIsoDate(new Date());
    return dates.some(d => formatUtcIsoDate(d.date) > todayStr);
  });
  readonly startDatePassed = computed(() => {
    const dates = this.project()?.installDates;
    if (!dates || dates.length === 0) return false;
    const earliest = dates.reduce((min, d) => d.date < min.date ? d : min).date;
    return formatUtcIsoDate(earliest) < formatLocalIsoDate(new Date());
  });
  /**
   * NonInstallable Services projects carry tax and non-work line items. They are never scheduled,
   * have no install dates, and have no checkpoints seeded at all (the server skips checkpoint
   * seeding for this type), so the install-date actions and the Review/Confirm/Utilities sections
   * have nothing to act on. Permits and Special Needs are gated on the same flag for the same
   * reason: both describe jobsite work this type never has.
   */
  readonly isNonInstallable = computed(
    () => this.project()?.projectTypeId === ProjectTypeConstants.NonInstallableServices);
  readonly hasUtilities = computed(() => (this.project()?.utilities?.length ?? 0) > 0);
  readonly nextCustomerSatisfactionVisit = computed(() => {
    const visits = this.project()?.customerSatisfactionVisits ?? [];
    const todayStr = formatLocalIsoDate(new Date());
    const upcoming = visits.filter(v => formatUtcIsoDate(v.date) >= todayStr);
    return upcoming.length > 0
      ? upcoming.reduce((earliest, v) => v.date < earliest.date ? v : earliest)
      : null;
  });

  readonly canScheduleCustomerSatisfactionVisit = computed(() => {
    const completionDate = this.project()?.completionDate;
    if (!completionDate) return false;
    const cutoff = new Date(completionDate);
    cutoff.setUTCDate(cutoff.getUTCDate() + this.configService.csvDelayDays);
    const todayStr = formatLocalIsoDate(new Date());
    const cutoffStr = formatUtcIsoDate(cutoff);
    return todayStr < cutoffStr;
  });

  private readonly completedCheckpointOverrides = signal<Map<string, ProjectCheckpointResponse>>(new Map());

  private readonly resolvedCheckpoints = computed(() => {
    const checkpoints = this.project()?.checkpoints ?? [];
    const overrides = this.completedCheckpointOverrides();
    return checkpoints.map(cp => {
      const override = overrides.get(cp.id);
      if (override) {
        return { ...cp, completedAt: override.completedAt, completedByName: override.completedBy } as ProjectCheckpointInfo;
      }
      return cp;
    });
  });

  readonly reviewCheckpoint = computed(() =>
    this.resolvedCheckpoints().find(cp => cp.projectCheckpointTypeId === ProjectCheckpointTypeConstants.Review)
  );

  readonly confirmationCheckpoint = computed(() =>
    this.resolvedCheckpoints().find(cp => cp.projectCheckpointTypeId === ProjectCheckpointTypeConstants.Confirmation)
  );

  readonly utilitiesCheckpoint = computed(() =>
    this.resolvedCheckpoints().find(cp => cp.projectCheckpointTypeId === ProjectCheckpointTypeConstants.Utilities)
  );

  isCheckpointCompleting = signal<Set<string>>(new Set());

  onCompleteCheckpoint(checkpoint: ProjectCheckpointInfo): void {
    if (checkpoint.completedAt || this.isCheckpointCompleting().has(checkpoint.id)) return;

    this.isCheckpointCompleting.update(s => new Set(s).add(checkpoint.id));

    this.apiClient.completeProjectCheckpoint(this.project().id, checkpoint.id).subscribe({
      next: (response) => {
        this.completedCheckpointOverrides.update(m => {
          const updated = new Map(m);
          updated.set(checkpoint.id, response);
          return updated;
        });
        this.isCheckpointCompleting.update(s => {
          const updated = new Set(s);
          updated.delete(checkpoint.id);
          return updated;
        });
        this.checkpointCompleted.emit(response);
      },
      error: () => {
        this.isCheckpointCompleting.update(s => {
          const updated = new Set(s);
          updated.delete(checkpoint.id);
          return updated;
        });
      },
    });
  }

  get projectActions(): CardFooterAction[] {
    const isComplete = this.isProjectComplete();
    const isReadOnly = this.isReadOnly();
    const isNonInstallable = this.isNonInstallable();
    const hasNextInstallDate = this.project()?.nextInstallDate != null;
    const completeBlockedByDates =
      !isNonInstallable && (!this.hasInstallDates() || this.hasFutureInstallDates());

    const candidates: { visible: boolean; item: CardFooterAction }[] = [
      {
        visible: !isComplete && !isNonInstallable && !hasNextInstallDate,
        item: { label: 'Add Install Date', type: 'secondary', action: this.onAddInstallDate.bind(this), disabled: isReadOnly },
      },
      {
        visible: !isComplete && !isNonInstallable && hasNextInstallDate,
        item: { label: 'View Install Dates', type: 'secondary', action: this.onViewInstallDates.bind(this), disabled: isReadOnly },
      },
      {
        visible: !isComplete,
        item: { label: 'Complete Project', type: 'primary', action: this.onCompleteProject.bind(this), disabled: isReadOnly || completeBlockedByDates },
      },
      // A NonInstallable Services project has no crew and nothing installed, so there is no visit
      // to schedule and nothing for a customer to be satisfied about — suppressed the same as the
      // install-date actions above.
      {
        visible: !isNonInstallable && this.canScheduleCustomerSatisfactionVisit() && this.nextCustomerSatisfactionVisit() === null,
        item: { label: 'Schedule Customer Satisfaction Visit', type: 'primary', action: this.onScheduleCustomerSatisfactionVisit.bind(this), disabled: isReadOnly },
      },
      {
        visible: !isNonInstallable && this.nextCustomerSatisfactionVisit() != null && this.canScheduleCustomerSatisfactionVisit(),
        item: { label: 'View Customer Satisfaction Visit', type: 'primary', action: this.onViewCustomerSatisfactionVisit.bind(this), disabled: isReadOnly },
      },
    ];

    return candidates.filter(({ visible }) => visible).map(({ item }) => item);
  }

  onAddInstallDate(): void {
    this.router.navigate(['/monthly-capacity'], {
      queryParams: {
        opportunityId: this.opportunityId(),
        projectId: this.project().id,
        branchId: this.branchId(),
      },
    });
  }

  onScheduleCustomerSatisfactionVisit(): void {
    this.router.navigate(['/monthly-capacity'], {
      queryParams: {
        opportunityId: this.opportunityId(),
        projectId: this.project().id,
        branchId: this.branchId(),
        customerSatisfactionVisit: true,
      },
    });
  }

  onViewCustomerSatisfactionVisit(): void {
    const visit = this.nextCustomerSatisfactionVisit();
    const queryParams: Record<string, string> = {
      branchId: this.branchId(),
    };
    if (visit?.date) queryParams['startDate'] = formatUtcIsoDate(visit.date);
    if (visit?.foremanUserId) queryParams['foremanId'] = visit.foremanUserId;
    this.router.navigate(['/calendar'], { queryParams });
  }

  // Splitting is only reachable through the completion flow (MOD-3972 Split & Complete):
  // the completion modal offers the option for eligible commercial projects and closes with
  // the 'split' sentinel; this component owns the data, so it opens the split modal.
  private openSplitProjectModal(): void {
    const project = this.project();
    const modalRef = this.modalService.openSplitProjectModal(
      project.id,
      this.buildSplittableProducts(project),
      this.job()?.taxes ?? [],
      this.buildCategoryByBoundProductId());
    modalRef.result.then(
      result => {
        if (result === true) {
          this.reloadPage();
        }
      },
      () => { /* dismissed without success — leave the page as-is */ },
    );
  }

  // The job's non-installable products live on a hidden NonInstallableServices sibling project;
  // the commercial split treats them as its own movable lines (MOD-4278). Dedupe by id: a row
  // moved by a previous split belongs to THIS project and is already in boundProducts. Raw rows
  // only (never the aggregated lists' synthetic rows) — the split keys everything by id.
  //
  // nonInstallableProducts is job-wide and categorized by the product's base mapping, so it also
  // carries rows sitting on VISIBLE projects — including the Commercial project a previous split
  // created. The server's movable set is this project's own rows plus active
  // NonInstallableServices siblings ONLY, so offering those would show an already-moved line as
  // movable and reject the save with a 400. JobInfo.Projects omits NonInstallableServices
  // projects, so "not on a visible project" is exactly "on the hidden sibling".
  private buildSplittableProducts(project: ProjectInfo): BoundProductInfo[] {
    const own = project.boundProducts ?? [];
    const ownIds = new Set(own.map(bp => bp.id));
    const visibleProjectIds = new Set((this.job()?.projects ?? []).map(p => p.id));
    const siblings = (this.job()?.nonInstallableProducts ?? []).filter(
      bp => !ownIds.has(bp.id) && !visibleProjectIds.has(bp.projectId ?? project.id));
    return [...own, ...siblings];
  }

  // The bound product rows carry no category; the job groups them into categorized lists, so
  // membership there is the category. Products absent from every list render under the
  // modal's fallback category.
  private buildCategoryByBoundProductId(): ReadonlyMap<string, string> {
    const job = this.job();
    const categories: [string, BoundProductInfo[] | undefined][] = [
      ['Installable Product', job?.installableProducts],
      ['Non-Installable Product', job?.nonInstallableProducts],
      ['Recurring Product', job?.recurringProducts],
    ];
    const map = new Map<string, string>();
    for (const [label, products] of categories) {
      for (const product of products ?? []) {
        map.set(product.id, label);
      }
    }
    return map;
  }

  onCompleteProject(): void {
    const project = this.project();
    const modalRef = this.modalService.openProjectCompletionModal(project.id, project.projectTypeName, {
      projectTypeId: project.projectTypeId,
      boundProducts: project.boundProducts ?? [],
    });
    modalRef.result.then(
      result => {
        if (result === true) {
          this.reloadPage();
        } else if (result === 'split') {
          this.openSplitProjectModal();
        }
      },
      () => { /* dismissed without success — leave the page as-is */ },
    );
  }

  protected reloadPage(): void {
    window.location.reload();
  }

  onViewInstallDates(): void {
    const nextInstallDate = this.project()?.nextInstallDate;
    const queryParams: Record<string, string> = {
      branchId: this.branchId(),
    };
    if (nextInstallDate?.date) queryParams['startDate'] = formatUtcIsoDate(nextInstallDate.date);
    if (nextInstallDate?.foremanUserId) queryParams['foremanId'] = nextInstallDate.foremanUserId;
    this.router.navigate(['/calendar'], { queryParams });
  }

  readonly projectDatesTableData = computed<GroundworksTableData | null>(() => {
    const installDates = [...(this.project().installDates ?? [])];
    const csvVisits = [...(this.project().customerSatisfactionVisits ?? [])];
    if (installDates.length === 0 && csvVisits.length === 0) return null;

    const fmt = (d: Date) =>
      `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}/${d.getUTCFullYear()}`;

    const byForeman = new Map<string, Date[]>();
    for (const entry of installDates) {
      const name = entry.foremanUserName ?? entry.foremanUserId ?? '—';
      if (!byForeman.has(name)) byForeman.set(name, []);
      byForeman.get(name)!.push(new Date(entry.date));
    }

    const allRanges: { foremanName: string; start: Date; end: Date }[] = [];
    for (const [foremanName, dates] of byForeman) {
      dates.sort((a, b) => a.getTime() - b.getTime());
      for (const date of dates) {
        const last = [...allRanges].reverse().find(r => r.foremanName === foremanName);
        if (last) {
          const nextDay = new Date(last.end);
          nextDay.setUTCDate(nextDay.getUTCDate() + 1);
          if (date.getTime() === nextDay.getTime()) {
            last.end = date;
            continue;
          }
        }
        allRanges.push({ foremanName, start: date, end: date });
      }
    }

    const installRows = allRanges.map(({ foremanName, start, end }) => ({
      start,
      dateStr: start.getTime() === end.getTime() ? fmt(start) : `${fmt(start)} - ${fmt(end)}`,
      type: 'Install',
      foreman: foremanName,
    }));

    const csvRows = csvVisits.map(v => ({
      start: new Date(v.date),
      dateStr: fmt(new Date(v.date)),
      type: 'CSV',
      foreman: v.foremanUserName ?? v.foremanUserId ?? '—',
    }));

    const rows: TableGroup['rows'] = [...installRows, ...csvRows]
      .sort((a, b) => a.start.getTime() - b.start.getTime())
      .map(r => ({
        type: 'data' as const,
        cells: [{ value: r.dateStr }, { value: r.type }, { value: r.foreman }],
      }));

    return {
      columns: [
        { label: 'Project Dates', dataType: 'text' },
        { label: 'Type', dataType: 'text' },
        { label: 'Foreman', dataType: 'text' },
      ],
      groups: [{ label: '', rows }],
    };
  });

  /**
   * True for BOTH ReadyToInvoice (2) and Invoiced (3): the business calls a project "invoiced" from
   * the moment it is ready to bill, so the two states share one label.
   *
   * `Complete` (1) is deliberately absent. It is not an invoicing action — the server stamps it
   * during ordinary project completion — so it renders no invoice pill here; the project's
   * disposition is reported separately by {@link projectStatusBadge} off `projectStatusId`, which its
   * `Completed` status always satisfies for a completed project. `None` (0) is the pre-lifecycle
   * default with nothing to report. Values above 3 are reserved by the server enum for a future
   * AR/write-off state and must stay silent here: mislabelling a money state is worse than showing
   * no badge, so this is an explicit two-value check rather than a `>= ReadyToInvoice` comparison
   * that would silently absorb them.
   */
  readonly isInvoiced = computed(() => {
    const status = this.project()?.invoiceStatus;
    return status === ProjectInvoiceStatus.ReadyToInvoice
      || status === ProjectInvoiceStatus.Invoiced;
  });

  /**
   * The card header's Project Status pill. Sourced from `ProjectInfo.projectStatusId` alone — the
   * lookup-table id populated by the Active/Cancelled/Descoped/Completed status cards (MOD-4988
   * through MOD-4991) and backfilled onto existing projects (MOD-4992) — so this reads the single
   * source of truth for project disposition rather than re-deriving it from `isCancelled`,
   * `isFullyDescoped`, or `completionDate`. Those three fields still drive `isReadOnly` and other
   * non-badge behavior elsewhere in this component and are intentionally left alone; only the badge
   * itself is rewired.
   *
   * Completed is the only arm with a date, reported from `completionDate`. Cancelled and Descoped
   * carry no date: `ProjectStatuses` is a lookup table, not a timestamped event log, and
   * `ProjectInfo` exposes no transition timestamp for either.
   *
   * The labels/colors live here rather than in a generated map because `ProjectStatusId` is a GUID
   * against a SQL lookup table, not a C# enum with `[Display]` attributes — there is nothing for
   * `scripts/generate-constants.js`'s `ENUM_LABEL_SOURCES` to read.
   */
  readonly projectStatusBadge = computed<StatusBadge | null>(() => {
    switch (this.project()?.projectStatusId) {
      case ProjectStatusConstants.Cancelled:
        return { label: 'Cancelled', type: 'danger', icon: 'bi bi-x-circle', size: 'large', date: null };
      case ProjectStatusConstants.Descoped:
        return { label: 'Descoped', type: 'indigo', icon: 'bi bi-x-circle', size: 'large', date: null };
      case ProjectStatusConstants.Completed:
        return {
          label: 'Completed', type: 'success', icon: 'bi bi-check-circle', size: 'large',
          date: this.badgeDate(this.project()?.completionDate),
        };
      default:
        // Active, unpopulated on a read path this migration hasn't reached yet, or a status id the
        // client doesn't recognize. Active is deliberately silent: it is the ordinary state of a
        // project in flight, not a disposition worth a pill. For the other two, silence is safer
        // than guessing a disposition.
        return null;
    }
  });

  /**
   * The card header's Invoice Status pill, rendered as its own badge alongside — never inside —
   * the Project Status pill. Invoice Status is sourced from a separate table
   * (`ProjectInfo.invoiceStatus`) and is unaffected by project status changes, so this logic is
   * carried over unchanged from the old combined `statusBadge`; only its rendering slot moved.
   *
   * There is no paid-in-full suppression here. The PAID IN FULL watermark and this pill are allowed
   * to appear together; the watermark reports payment, the pill reports invoice state, and they are
   * not the same claim.
   *
   * The label lives here rather than in a generated map because the server enum carries no
   * `[Display]` attributes; adding them (and registering the enum in `ENUM_LABEL_SOURCES` in
   * `scripts/generate-constants.js`) is what would move label ownership to the server.
   */
  readonly invoiceStatusBadge = computed<StatusBadge | null>(() => {
    if (!this.isInvoiced()) return null;
    return {
      label: 'Invoiced', type: 'magenta', icon: 'bi bi-receipt', size: 'large',
      date: this.badgeDate(this.project()?.invoiceDate),
    };
  });

  private toDisplayDay(value: Date): Date {
    const isStoredDate =
      value.getUTCHours() === 0 && value.getUTCMinutes() === 0 &&
      value.getUTCSeconds() === 0 && value.getUTCMilliseconds() === 0;

    return isStoredDate
      ? value
      : new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()));
  }

  private badgeDate(value: Date | undefined): Date | null {
    return value ? this.toDisplayDay(value) : null;
  }

  readonly headerTitle = computed(
    () => this.project().displayLabel ?? this.project().projectTypeName ?? 'Project'
  );

  // The Business Central project id shown in light grey next to the project type. A project with no
  // external-identifier row yet (expected before the BC backfill) shows the em-dash placeholder.
  readonly headerBcProjectId = computed(() => {
    const bcProjectId = this.project().bcProjectId;
    return bcProjectId == null ? '—' : String(bcProjectId);
  });

  private readonly specialNeedOverrides = signal<ProjectSpecialNeedInfo[]>([]);
  private readonly specialNeedCompletionOverrides = signal<Map<string, boolean>>(new Map());
  private readonly specialNeedCompleting = signal<Set<string>>(new Set());

  private resolvedSpecialNeeds(): ProjectSpecialNeedInfo[] {
    const fromProject = this.project()?.specialNeeds ?? [];
    const added = this.specialNeedOverrides();
    const completionOverrides = this.specialNeedCompletionOverrides();
    return [...fromProject, ...added].map(n => {
      const override = completionOverrides.get(n.id);
      if (override !== undefined) {
        const patched = new ProjectSpecialNeedInfo(n);
        patched.isCompleted = override;
        return patched;
      }
      return n;
    });
  }

  readonly specialNeedsTableData = computed<GroundworksTableData | null>(() => {
    const needs = this.resolvedSpecialNeeds();
    this.specialNeedCompleting();
    if (needs.length === 0) return null;

    const fmt = (d: Date) =>
      `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}/${d.getUTCFullYear()}`;

    const isComplete = this.isProjectComplete();
    const todayStr = formatLocalIsoDate(new Date());
    const installDates = this.project()?.installDates;
    const startDatePassed = installDates && installDates.length > 0
      && formatUtcIsoDate(installDates.reduce((min, d) => d.date < min.date ? d : min).date) < todayStr;
    const rows = needs.map(n => {
      return {
        type: 'data' as const,
        cells: [
          { value: n.details ?? '' },
          { value: n.assignedToName ?? '—' },
          { value: n.dueDate ? fmt(new Date(n.dueDate)) : '—' },
          {
              value: '',
              actionButton: {
                icon: 'bi bi-pencil',
                ariaLabel: 'Edit special need',
                variant: 'primary' as const,
                action: () => this.onEditSpecialNeed(n),
                disabled: !!(n.isCompleted || isComplete || startDatePassed),
              },
            },
          {
            value: '',
            checkbox: {
              ariaLabel: n.isCompleted ? 'Completed' : 'Mark as completed',
              checked: n.isCompleted ?? false,
              disabled: isComplete || n.isCompleted,
              onChange: (checked: boolean) => { if (checked) this.onCompleteSpecialNeed(n); },
            },
          },
        ],
      };
    });

    return {
      columns: [
        { label: 'Item', dataType: 'text' },
        { label: 'Assigned', dataType: 'text' },
        { label: 'Remind', dataType: 'text' },
        { label: 'Edit', dataType: 'action-menu', align: 'center' },
        { label: 'Complete', dataType: 'checkbox', align: 'center' },
      ],
      groups: [{ label: '', rows }],
    };
  });

  onCompleteSpecialNeed(need: ProjectSpecialNeedInfo): void {
    if (need.isCompleted || this.specialNeedCompleting().has(need.id)) return;

    this.specialNeedCompleting.update(s => new Set(s).add(need.id));

    const request = new UpdateProjectSpecialNeedRequest({ isCompleted: true });
    this.apiClient.updateProjectSpecialNeed(this.project().id, need.id, request).subscribe({
      next: () => {
        this.specialNeedCompletionOverrides.update(m => {
          const updated = new Map(m);
          updated.set(need.id, true);
          return updated;
        });
        this.specialNeedCompleting.update(s => {
          const updated = new Set(s);
          updated.delete(need.id);
          return updated;
        });
      },
      error: () => {
        this.specialNeedCompleting.update(s => {
          const updated = new Set(s);
          updated.delete(need.id);
          return updated;
        });
      },
    });
  }

  readonly soonestSpecialNeedDueDate = computed<Date | null>(() => {
    const needs = this.resolvedSpecialNeeds();
    const todayStr = formatLocalIsoDate(new Date());
    const upcoming = needs
      .filter(n => !n.isCompleted && n.dueDate && formatUtcIsoDate(n.dueDate) >= todayStr);
    if (upcoming.length === 0) return null;
    return upcoming.reduce((earliest, n) => n.dueDate! < earliest.dueDate! ? n : earliest).dueDate!;
  });

  readonly specialNeedActions = computed<CardFooterAction[]>(() => {
    if (this.isReadOnly()) return [];
    return [{ label: '+ Special Need', type: 'outline-secondary', action: this.onAddSpecialNeed.bind(this) }];
  });

  onEditSpecialNeed(need: ProjectSpecialNeedInfo): void {
    const modalRef = this.modalService.openSpecialNeedEditModal(
      this.project().id, this.branchId(), need);

    modalRef.result.then(
      () => this.reloadPage(),
      () => { /* dismissed */ },
    );
  }

  onAddSpecialNeed(): void {
    const modalRef = this.modalService.openSpecialNeedModal(this.project().id, this.branchId());
    modalRef.result.then(
      (result) => {
        if (result) {
          this.specialNeedOverrides.update(current => [...current, result]);
        }
      },
      () => { /* dismissed */ },
    );
  }

  private readonly utilitiesNotNeededOverride = signal<boolean | null>(null);
  readonly utilitiesNotNeeded = computed(() =>
    this.utilitiesNotNeededOverride() ?? this.project()?.utilitiesNotNeeded ?? false
  );
  readonly isUtilitiesNotNeededUpdating = signal(false);

  onToggleUtilitiesNotNeeded(checked: boolean): void {
    if (this.isUtilitiesNotNeededUpdating()) return;
    this.isUtilitiesNotNeededUpdating.set(true);

    const request = new UpdateProjectUtilitiesNotNeededRequest({ utilitiesNotNeeded: checked });
    this.apiClient.updateProjectUtilitiesNotNeeded(this.project().id, request).subscribe({
      next: () => {
        this.utilitiesNotNeededOverride.set(checked);
        this.isUtilitiesNotNeededUpdating.set(false);
      },
      error: () => {
        this.isUtilitiesNotNeededUpdating.set(false);
      },
    });
  }

  private readonly utilityOverrides = signal<ProjectUtilityInfo[]>([]);
  private readonly utilityCompletionOverrides = signal<Map<string, boolean>>(new Map());
  private readonly utilityCompleting = signal<Set<string>>(new Set());

  private resolvedUtilities(): ProjectUtilityInfo[] {
    const fromProject = this.project()?.utilities ?? [];
    const added = this.utilityOverrides();
    const completionOverrides = this.utilityCompletionOverrides();
    return [...fromProject, ...added].map(u => {
      const override = completionOverrides.get(u.id);
      if (override !== undefined) {
        const patched = new ProjectUtilityInfo(u);
        patched.isCompleted = override;
        return patched;
      }
      return u;
    });
  }

  readonly utilitiesTableData = computed<GroundworksTableData | null>(() => {
    const utilities = this.resolvedUtilities();
    this.utilityCompleting();
    if (utilities.length === 0) return null;

    const fmt = (d: Date) =>
      `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}/${d.getUTCFullYear()}`;

    const isComplete = this.isProjectComplete();
    const todayStr = formatLocalIsoDate(new Date());
    const installDates = this.project()?.installDates;
    const startDatePassed = installDates && installDates.length > 0
      && formatUtcIsoDate(installDates.reduce((min, d) => d.date < min.date ? d : min).date) < todayStr;
    const rows = utilities.map(u => ({
      type: 'data' as const,
      cells: [
        { value: u.description ?? '' },
        { value: u.calledInDate ? fmt(new Date(u.calledInDate)) : '—' },
        { value: u.digWindowStartDate ? fmt(new Date(u.digWindowStartDate)) : '—' },
        { value: u.digWindowEndDate ? fmt(new Date(u.digWindowEndDate)) : '—' },
        { value: u.confirmationNumber ?? '—' },
        {
          value: '',
          actionButton: {
            icon: 'bi bi-pencil',
            ariaLabel: 'Edit utility',
            variant: 'primary' as const,
            action: () => this.onEditUtility(u),
            disabled: !!(u.isCompleted || isComplete || startDatePassed),
          },
        },
        {
          value: '',
          checkbox: {
            ariaLabel: u.isCompleted ? 'Completed' : 'Mark as completed',
            checked: u.isCompleted ?? false,
            disabled: isComplete || u.isCompleted,
            onChange: (checked: boolean) => { if (checked) this.onCompleteUtility(u); },
          },
        },
      ],
    }));

    return {
      columns: [
        { label: 'Description', dataType: 'text' },
        { label: 'Called In', dataType: 'text' },
        { label: 'Dig Start', dataType: 'text' },
        { label: 'Dig End', dataType: 'text' },
        { label: 'Confirmation #', dataType: 'text' },
        { label: 'Edit', dataType: 'action-menu', align: 'center' },
        { label: 'Complete', dataType: 'checkbox', align: 'center' },
      ],
      groups: [{ label: '', rows }],
    };
  });

  onCompleteUtility(utility: ProjectUtilityInfo): void {
    if (utility.isCompleted || this.utilityCompleting().has(utility.id)) return;

    this.utilityCompleting.update(s => new Set(s).add(utility.id));

    const request = new UpdateProjectUtilityRequest({ isCompleted: true });
    this.apiClient.updateProjectUtility(this.project().id, utility.id, request).subscribe({
      next: () => {
        this.utilityCompletionOverrides.update(m => {
          const updated = new Map(m);
          updated.set(utility.id, true);
          return updated;
        });
        this.utilityCompleting.update(s => {
          const updated = new Set(s);
          updated.delete(utility.id);
          return updated;
        });
      },
      error: () => {
        this.utilityCompleting.update(s => {
          const updated = new Set(s);
          updated.delete(utility.id);
          return updated;
        });
      },
    });
  }

  readonly utilityActions = computed<CardFooterAction[]>(() => {
    if (this.isReadOnly() || (this.project().utilitiesRequired && this.utilitiesNotNeeded())) return [];
    return [{ label: '+ Utility', type: 'outline-secondary', action: this.onAddUtility.bind(this) }];
  });

  onEditUtility(utility: ProjectUtilityInfo): void {
    const modalRef = this.modalService.openUtilityEditModal(
      this.project().id, utility);

    modalRef.result.then(
      () => this.reloadPage(),
      () => { /* dismissed */ },
    );
  }

  onAddUtility(): void {
    const modalRef = this.modalService.openUtilityModal(this.project().id);
    modalRef.result.then(
      (result) => {
        if (result) {
          this.utilityOverrides.update(current => [...current, result]);
        }
      },
      () => { /* dismissed */ },
    );
  }

  private readonly aggregatedProducts = computed(() =>
    aggregateBoundProducts(this.project()?.boundProducts ?? [])
  );

  /**
   * The job taxes this project is the Business Central billing anchor for. Tax reaches BC as an
   * explicit NONTAXABLE sales-order line hung off the project named by TaxData.ProjectId, and that
   * anchor is a NonInstallableServices project unless the job is commercial — see TaxProjectRule
   * on the server. Only the NonInstallable card surfaces it, so a commercial job's tax stays on
   * the job card alone.
   *
   * The projectId match is strict: a tax predating the anchor mapping has none, and guessing an
   * owner for it would attribute the figure to a project BC is not billing it against. Zero
   * amounts are dropped for the same reason the payments panel drops them — a $0.00 row is noise.
   */
  readonly projectTaxes = computed<TaxInfo[]>(() => {
    if (!this.isNonInstallable()) return [];
    const projectId = this.project()?.id;
    return (this.job()?.taxes ?? []).filter(
      t => t.projectId === projectId && (t.amount ?? 0) !== 0);
  });

  readonly projectTableData = computed(() => {
    const products = this.aggregatedProducts();
    const taxes = this.projectTaxes();
    // A tax project spared from descope keeps its tax but loses every product row, and hiding the
    // table there would hide the only thing left to see.
    if (products.length === 0 && taxes.length === 0) return null;
    const project = this.project();
    const grossTotal = project?.projectGrossAmount ?? products.reduce((sum, p) => sum + (p.grossAmount ?? 0), 0);
    const netTotal = project?.projectNetAmount ?? products.reduce((sum, p) => sum + (p.netAmount ?? 0), 0);
    return buildProductTableData('Installable Product', products, grossTotal, netTotal, true, taxes);
  });

  private readonly permitsReloadTrigger = signal(0);
  readonly permitsLoadError = signal<string | null>(null);

  private readonly permitsFromApi = toSignal(
    combineLatest([toObservable(this.project), toObservable(this.permitsReloadTrigger)]).pipe(
      switchMap(([p]) => {
        // A non-installable project's Permits card is gated out of the template entirely, so
        // fetching its permits would be a request whose result nothing can render.
        if (!this.configService.enablePermitting
          || p.projectTypeId === ProjectTypeConstants.NonInstallableServices) {
          return of([] as PermitListItem[]);
        }
        this.permitsLoadError.set(null);
        return this.apiClient.getPermitsByProject(p.id).pipe(
          catchError(err => {
            console.error('Failed to load permits', err);
            this.permitsLoadError.set('Unable to load permits. Try refreshing.');
            return of([] as PermitListItem[]);
          })
        );
      }),
    ),
  );

  readonly permitsLoaded = computed(() => this.permitsFromApi() !== undefined);

  readonly shouldAutoExpandPermits = computed(() =>
    this.permitsFromApi()?.some(p => !p.isCompleted) ?? false
  );

  /**
   * MOD-3777 — visual risk indicator on the Permits card header.
   *  - 'none'   = no risk to flag (all permits Approved & Open, OR project is
   *               cancelled/descoped/complete, OR no permits, OR no install dates,
   *               OR start is &gt; PERMITS_YELLOW_WINDOW_DAYS away)
   *  - 'yellow' = pre-start risk: project starts within PERMITS_YELLOW_WINDOW_DAYS
   *               and some permit isn't Approved & Open (AC 1)
   *  - 'red'    = post-start critical: project already started and some permit isn't
   *               Approved & Open (AC 2)
   * "Approved & Open" matches PermitCalendarSummary.AllApprovedAndOpen — every
   * permit's Phase-4 PermitStatus equals ApprovedAndOpen.
   */
  private static readonly PERMITS_YELLOW_WINDOW_DAYS = 5;
  readonly permitsHighlight = computed<'none' | 'yellow' | 'red'>(() => {
    if (this.isReadOnly() || this.isProjectComplete()) return 'none';
    const permits = this.permitsFromApi();
    if (!permits || permits.length === 0) return 'none';
    if (!this.hasInstallDates()) return 'none';
    const allApprovedAndOpen = permits.every(p => p.permitStatus === PermitStatus.ApprovedAndOpen);
    if (allApprovedAndOpen) return 'none';
    if (this.startDatePassed()) return 'red';
    return this.daysUntilStart() <= ProjectComponent.PERMITS_YELLOW_WINDOW_DAYS ? 'yellow' : 'none';
  });

  /** Whole-day count between today (local) and the earliest install date (UTC). */
  private readonly daysUntilStart = computed<number>(() => {
    const dates = this.project()?.installDates;
    if (!dates || dates.length === 0) return Number.POSITIVE_INFINITY;
    const earliestStr = formatUtcIsoDate(dates.reduce((min, d) => d.date < min.date ? d : min).date);
    const todayStr = formatLocalIsoDate(new Date());
    const dayMs = 24 * 60 * 60 * 1000;
    return Math.floor((Date.parse(earliestStr) - Date.parse(todayStr)) / dayMs);
  });

  readonly permitsTableData = computed<GroundworksTableData | null>(() => {
    const permits = this.permitsFromApi();
    if (!permits || permits.length === 0) return null;

    const fmt = (d: Date) =>
      `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}/${d.getUTCFullYear()}`;

    const rows = permits.map(p => ({
      type: 'data' as const,
      cells: [
        { value: p.permitNumber ?? '—' },
        { value: p.statusLabel ?? '—' },
        { value: p.nextActionDate ? fmt(new Date(p.nextActionDate)) : '—' },
        {
          value: '',
          actionButton: {
            icon: 'bi bi-pencil',
            ariaLabel: 'Edit permit',
            variant: 'primary' as const,
            action: () => this.onEditPermit(p),
          },
        },
      ],
    }));

    return {
      columns: [
        { label: 'Permit Number', dataType: 'text' },
        { label: 'Status', dataType: 'text' },
        { label: 'Action Due', dataType: 'text' },
        { label: 'Edit', dataType: 'action-menu', align: 'center' },
      ],
      groups: [{ label: '', rows }],
    };
  });

  readonly permitsActions = computed<CardFooterAction[]>(() => {
    if (this.isReadOnly()) return [];
    return [{ label: '+ Permit', type: 'outline-secondary', action: this.onAddPermit.bind(this) }];
  });

  onAddPermit(): void {
    const modalRef = this.modalService.openPermitCreateModal(this.project().id, this.branchId());
    modalRef.result.then(
      result => {
        if (result) {
          this.permitsReloadTrigger.update(n => n + 1);
          this.permitSaved.emit();
        }
      },
      () => { /* dismissed */ },
    );
  }

  onEditPermit(permit: PermitListItem): void {
    const modalRef = this.modalService.openPermitEditModal(this.project().id, permit.id, this.branchId());
    modalRef.result.then(
      result => {
        if (result) {
          this.permitsReloadTrigger.update(n => n + 1);
          this.permitSaved.emit();
        }
      },
      () => { /* dismissed */ },
    );
  }
}
